import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SearchStructuredDataFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchStructuredDataFragmentFragmentDoc';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';

/* GraphQL */ `
  fragment SearchStructuredDataFragment on SearchProductResult {
    items {
      brand {
        name
      }
      efficiencyFlags {
        type
        text
      }
      styleName
      ratings {
        averageValue
        count
      }
      primaryVariationGroup {
        image
        href
        articleNumberWithPromoCode
        price {
          currency
          currentPrice
          unitPackage {
            amount
            price
            unit
          }
        }
      }
    }
  }
`;

export type SearchStructuredDataProps = {
  maskedData: FragmentType<typeof SearchStructuredDataFragmentFragmentDoc>;
};

type EnergyEfficiencyFlagText = 'A+++' | 'A++' | 'A+' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

const mapEnergyEfficiencyFlagTextToSchemaLink: Record<EnergyEfficiencyFlagText, string> = {
  'A+++': 'https://schema.org/EUEnergyEfficiencyCategoryA3Plus',
  'A++': 'https://schema.org/EUEnergyEfficiencyCategoryA2Plus',
  'A+': 'https://schema.org/EUEnergyEfficiencyCategoryA1Plus',
  A: 'https://schema.org/EUEnergyEfficiencyCategoryA',
  B: 'https://schema.org/EUEnergyEfficiencyCategoryB',
  C: 'https://schema.org/EUEnergyEfficiencyCategoryC',
  D: 'https://schema.org/EUEnergyEfficiencyCategoryD',
  E: 'https://schema.org/EUEnergyEfficiencyCategoryE',
  F: 'https://schema.org/EUEnergyEfficiencyCategoryF',
  G: 'https://schema.org/EUEnergyEfficiencyCategoryG',
};

const stripQueryAndHash = (urlOrPath: string) => urlOrPath.split(/[?#]/, 1)[0];

const PRODUCT_LIST_LIMIT = 10;

/**
 * Structured data script for the first 10 Products in SERP & MBA.
 * */
export const SearchStructuredData = ({ maskedData }: SearchStructuredDataProps) => {
  const data = unmask<typeof SearchStructuredDataFragmentFragmentDoc>(maskedData);
  const { host } = useConfig();

  if (!data || !data.items || (data && data.items && data.items.length === 0)) {
    return null;
  }

  const structuredData = {
    '@context': 'http://schema.org',
    '@type': 'ItemList',
    itemListElement: data.items.slice(0, PRODUCT_LIST_LIMIT).map((productItem, productIndex) => {
      const priceObject = productItem.primaryVariationGroup.price;

      const currentPrice = priceObject.currentPrice / 100;
      const { currency } = priceObject;

      const href = stripQueryAndHash(productItem.primaryVariationGroup.href);
      const url = `https://www.${host.domain}${href}`;

      const lowestEnergyEfficiencyFlag = productItem.efficiencyFlags
        ?.filter((flag) => flag.type === 'ENERGY' || flag.type === 'ENERGY_OLD')
        .sort(
          (a, b) =>
            Object.keys(mapEnergyEfficiencyFlagTextToSchemaLink).indexOf(b.text) -
            Object.keys(mapEnergyEfficiencyFlagTextToSchemaLink).indexOf(a.text),
        )[0];

      const tireEfficiencyFlags = productItem.efficiencyFlags?.filter(
        (flag) =>
          flag.type === 'EXTERNAL_ROLLING_NOISE' ||
          flag.type === 'FUEL' ||
          flag.type === 'WET_SURFACE_GRIP',
      );

      return {
        '@type': 'ListItem',
        position: productIndex + 1,
        item: {
          '@type': 'Product',
          name: `${productItem.brand.name} ${productItem.styleName}`,
          image: [productItem.primaryVariationGroup.image],
          ...(lowestEnergyEfficiencyFlag
            ? {
                hasEnergyConsumptionDetails: {
                  '@type': 'EnergyConsumptionDetails',
                  ...(lowestEnergyEfficiencyFlag.type === 'ENERGY'
                    ? {
                        energyEfficiencyScaleMax: 'https://schema.org/EUEnergyEfficiencyCategoryA',
                        energyEfficiencyScaleMin: 'https://schema.org/EUEnergyEfficiencyCategoryG',
                      }
                    : {}),
                  hasEnergyEfficiencyCategory:
                    mapEnergyEfficiencyFlagTextToSchemaLink[
                      lowestEnergyEfficiencyFlag.text as EnergyEfficiencyFlagText
                    ],
                },
              }
            : {}),
          offers: {
            '@type': 'Offer',
            price: currentPrice,
            priceCurrency: currency,
            priceSpecification: [
              {
                '@type': 'PriceSpecification',
                price: currentPrice,
                priceCurrency: currency,
              },
              ...(priceObject.unitPackage
                ? [
                    {
                      '@type': 'UnitPriceSpecification',
                      price: priceObject.unitPackage.price / 100,
                      priceCurrency: currency,
                      referenceQuantity: {
                        '@type': 'QuantitativeValue',
                        value: priceObject.unitPackage.amount,
                        unitText: priceObject.unitPackage.unit,
                      },
                    },
                  ]
                : []),
            ],
          },
          ...(tireEfficiencyFlags && tireEfficiencyFlags.length > 0
            ? {
                additionalProperty: [
                  tireEfficiencyFlags.map((flag) => ({
                    '@type': 'PropertyValue',
                    name: flag.type,
                    value: flag.text,
                  })),
                ],
              }
            : {}),
          ...(productItem.ratings
            ? {
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: productItem.ratings?.averageValue,
                  reviewCount: productItem.ratings?.count,
                },
              }
            : {}),
          url,
        },
      };
    }),
  };

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData),
      }}
    />
  );
};
